import { Badge, Card, CardContent, CardHeader, CardTitle, EmptyState, QueryStateBoundary } from "@/components/ui";
import { formatDateTime } from "@/features/outreach/format";
import { useContractEvents } from "@/api/hooks/useContractEvents";
import {
  CONTRACT_EVENT_TYPE_BADGE_VARIANT,
  CONTRACT_EVENT_TYPE_LABELS,
  type ContractEvent,
} from "@/api/types/contractEvents";

interface ContractTimelineProps {
  contractId: string | undefined;
}

/**
 * ContractTimeline — the append-only signature/audit trail for one contract
 * (V2 Builder 3: GET /v2/contracts/{id}/events). Not mounted anywhere by
 * this builder — the contracts feature has no owned detail view yet in
 * this worktree, so this is exported for the orchestrator (or whichever
 * builder owns the contract/event detail page) to drop into place, e.g.
 * inside an EventDetail/ContractDetail card next to the existing
 * send/sign/void actions.
 */
export function ContractTimeline({ contractId }: ContractTimelineProps) {
  const { data: events, isLoading, error, refetch } = useContractEvents(contractId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Signature trail</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
          {!events || events.length === 0 ? (
            <EmptyState
              icon="📜"
              title="No activity yet"
              description="Actions on this contract (issued, sent, viewed, signed, voided) will appear here."
            />
          ) : (
            <ol className="space-y-3">
              {events.map((event) => (
                <ContractTimelineRow key={event.id} event={event} />
              ))}
            </ol>
          )}
        </QueryStateBoundary>
      </CardContent>
    </Card>
  );
}

function ContractTimelineRow({ event }: { event: ContractEvent }) {
  const who = event.signer_name ?? event.actor ?? null;

  return (
    <li className="flex items-start justify-between gap-4 border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Badge variant={CONTRACT_EVENT_TYPE_BADGE_VARIANT[event.event_type]} size="sm">
            {CONTRACT_EVENT_TYPE_LABELS[event.event_type]}
          </Badge>
          {who && <span className="text-sm text-text-secondary">{who}</span>}
        </div>
        {event.signer_ip && (
          <span className="text-xs text-text-muted">
            {event.signer_ip}
            {event.signer_email ? ` · ${event.signer_email}` : ""}
          </span>
        )}
      </div>
      <time dateTime={event.created_at} className="shrink-0 text-xs text-text-muted">
        {formatDateTime(event.created_at)}
      </time>
    </li>
  );
}
